import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Link2,
  Plus,
  Search,
  Unlink,
} from 'lucide-react';
import { Modal } from '../reusable';
import API_BASE_URL from '../../config/api';
import { authFetch } from '../../services/apiClient';
import { buildQuotationPdfObjectUrl } from '../../utils/quotationPdf';
import { QUOTATION_PREVIEW_SIDE_PANEL_WIDTH } from '../../constants/quotationPreviewPanel';
import { CHILE_MOBILE_PLACEHOLDER, PHONE_MAX_LENGTH, formatChileanMobile, isChileanPhoneField } from '../../utils/textUtils';
import { parseDateSafe } from './periodSummaryUtils';
import { resolveHasPurchaseOrder } from './salePurchaseOrderUtils';
import { getBillingSectionStatus } from './saleBillingUtils';
import {
  BILLING_DOC_TYPE_OPTIONS,
  REGISTER_BILLING_OPTION,
  billingSelectionToIds,
  documentAmountMatchesSaleTotal,
  formatBillingItemsSummary,
  formatItemQuantity,
  getAllSaleItemSelection,
  getAvailableItemQuantity,
  getBillingDocumentAmount,
  getClaimedBillingItemQuantities,
  getItemLineGrossForQuantity,
  getSelectableBillingItems,
  hasBillingSelection,
  mergeBillingDocuments,
  sumSelectedItemsGross,
} from './saleBillingDocumentUtils';

const SECTION_KEYS = ['purchase', 'dispatch', 'billing', 'payments', 'notes'];

const DOC_TYPE_HINTS = [
  { value: '', label: 'Todos' },
  { value: 'factura', label: 'Factura' },
  { value: 'boleta', label: 'Boleta' },
  { value: 'crédito', label: 'Nota de crédito' },
  { value: 'credito', label: 'Nota de crédito' },
  { value: 'débito', label: 'Nota de débito' },
  { value: 'debito', label: 'Nota de débito' },
];

const toYmd = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDateEs = (value) => {
  if (!value) return '';
  const d = parseDateSafe(value);
  if (!d) return '';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const joinHeaderParts = (parts) =>
  parts.map((part) => String(part || '').trim()).filter(Boolean).join(' · ');

const inputBase = (isDarkMode) => ({
  width: '100%',
  borderRadius: '8px',
  border: `1px solid ${isDarkMode ? '#3f3f46' : '#d1d5db'}`,
  background: isDarkMode ? '#111113' : '#ffffff',
  color: isDarkMode ? '#f4f4f5' : '#111827',
  padding: '8px 10px',
  fontSize: '13px',
  boxSizing: 'border-box',
});

const btnGhost = (isDarkMode) => ({
  height: '32px',
  padding: '0 10px',
  borderRadius: '8px',
  border: `1px solid ${isDarkMode ? '#3f3f46' : '#d1d5db'}`,
  background: isDarkMode ? '#18181b' : '#ffffff',
  color: isDarkMode ? '#f4f4f5' : '#111827',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  whiteSpace: 'nowrap',
});

const btnPrimary = {
  height: '32px',
  padding: '0 10px',
  borderRadius: '8px',
  border: 'none',
  background: '#2563eb',
  color: '#fff',
  fontSize: '12px',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  whiteSpace: 'nowrap',
};

const dateText = (value) => {
  const d = parseDateSafe(value);
  if (!d) return '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const money = (value, currency = 'CLP') => {
  const n = Number(value || 0);
  const sym = currency === 'USD' ? 'US$' : currency === 'EUR' ? 'EUR' : '$';
  return `${sym} ${n.toLocaleString('es-CL')}`;
};

const grossAmountConIva = (netTotal) => {
  const t = Number(netTotal || 0);
  if (!Number.isFinite(t) || t === 0) return 0;
  return Math.round(t * 1.19 * 100) / 100;
};

const isFilledValue = (value, { treatZeroAsEmpty = false } = {}) => {
  if (value == null) return false;
  const text = String(value).trim();
  if (!text) return false;
  if (treatZeroAsEmpty && Number(text) === 0) return false;
  return true;
};

const getFillStatus = (values) => {
  const total = values.length;
  const filled = values.filter(Boolean).length;
  if (total === 0 || filled === 0) return 'empty';
  if (filled >= total) return 'complete';
  return 'partial';
};

const FILL_STATUS_META = {
  complete: {
    label: 'Completo',
    bgLight: '#dcfce7',
    bgDark: 'rgba(22, 163, 74, 0.22)',
    colorLight: '#166534',
    colorDark: '#86efac',
    borderLight: '#86efac',
    borderDark: 'rgba(134, 239, 172, 0.45)',
  },
  partial: {
    label: 'Parcial',
    bgLight: '#ffedd5',
    bgDark: 'rgba(234, 88, 12, 0.22)',
    colorLight: '#9a3412',
    colorDark: '#fdba74',
    borderLight: '#fdba74',
    borderDark: 'rgba(253, 186, 116, 0.45)',
  },
  empty: {
    label: 'Sin información',
    bgLight: '#f3f4f6',
    bgDark: 'rgba(113, 113, 122, 0.28)',
    colorLight: '#4b5563',
    colorDark: '#d4d4d8',
    borderLight: '#d1d5db',
    borderDark: 'rgba(161, 161, 170, 0.45)',
  },
  no_po: {
    label: 'Sin orden de compra',
    bgLight: '#e0e7ff',
    bgDark: 'rgba(99, 102, 241, 0.22)',
    colorLight: '#3730a3',
    colorDark: '#c7d2fe',
    borderLight: '#a5b4fc',
    borderDark: 'rgba(165, 180, 252, 0.45)',
  },
  invoiced: {
    label: 'Facturado',
    bgLight: '#dcfce7',
    bgDark: 'rgba(22, 163, 74, 0.22)',
    colorLight: '#166534',
    colorDark: '#86efac',
    borderLight: '#86efac',
    borderDark: 'rgba(134, 239, 172, 0.45)',
  },
  invoiced_partial: {
    label: 'Facturado Parcial',
    bgLight: '#ffedd5',
    bgDark: 'rgba(234, 88, 12, 0.22)',
    colorLight: '#9a3412',
    colorDark: '#fdba74',
    borderLight: '#fdba74',
    borderDark: 'rgba(253, 186, 116, 0.45)',
  },
};

const StatusPill = ({ status, isDarkMode }) => {
  const meta = FILL_STATUS_META[status] || FILL_STATUS_META.empty;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 700,
        background: isDarkMode ? meta.bgDark : meta.bgLight,
        color: isDarkMode ? meta.colorDark : meta.colorLight,
        border: `1px solid ${isDarkMode ? meta.borderDark : meta.borderLight}`,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: isDarkMode ? meta.colorDark : meta.colorLight,
          flexShrink: 0,
        }}
      />
      {meta.label}
    </span>
  );
};

const SECTION_HEADER_MIN_HEIGHT = '48px';

const CollapsibleSection = ({
  title,
  status,
  isOpen,
  onToggle,
  isDarkMode,
  expandable = true,
  headerAddon = null,
  headerActions = null,
  children,
}) => {
  const border = isDarkMode ? '#3f3f46' : '#e5e7eb';
  const headerBg = isDarkMode ? '#18181b' : '#f9fafb';
  const text = isDarkMode ? '#f4f4f5' : '#111827';
  const canExpand = expandable !== false;
  const isExpanded = canExpand && isOpen;

  const titleBlock = (
    <>
      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: text, flexShrink: 0, lineHeight: 1.2 }}>
        {title}
      </h4>
      <StatusPill status={status} isDarkMode={isDarkMode} />
      {headerAddon ? (
        <span
          style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          {headerAddon}
        </span>
      ) : null}
      <span style={{ flex: 1, minWidth: 0 }} aria-hidden />
    </>
  );

  const chevronToggleStyle = {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    borderRadius: '6px',
  };

  return (
    <section
      style={{
        border: `1px solid ${border}`,
        borderRadius: '10px',
        overflow: 'hidden',
        background: isDarkMode ? '#111113' : '#ffffff',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minHeight: SECTION_HEADER_MIN_HEIGHT,
          padding: '8px 12px',
          boxSizing: 'border-box',
          background: headerBg,
          borderBottom: isExpanded ? `1px solid ${border}` : 'none',
        }}
      >
        {canExpand ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isExpanded}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minWidth: 0,
              minHeight: '32px',
              padding: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {titleBlock}
          </button>
        ) : (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              minWidth: 0,
              minHeight: '32px',
            }}
          >
            {titleBlock}
          </div>
        )}
        {headerActions ? (
          <div
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            {headerActions}
          </div>
        ) : null}
        {canExpand ? (
          <button
            type="button"
            onClick={onToggle}
            aria-label={isExpanded ? 'Colapsar sección' : 'Expandir sección'}
            aria-expanded={isExpanded}
            style={chevronToggleStyle}
          >
            {isExpanded ? <ChevronUp size={16} color={text} /> : <ChevronDown size={16} color={text} />}
          </button>
        ) : (
          <span style={{ width: '28px', flexShrink: 0 }} aria-hidden />
        )}
      </div>
      {isExpanded ? <div style={{ padding: '12px' }}>{children}</div> : null}
    </section>
  );
};

const FieldLabel = ({ children, muted }) => (
  <div style={{ fontSize: '11px', fontWeight: 600, color: muted, marginBottom: 4 }}>{children}</div>
);

const FormField = ({ label, muted, children, colSpan }) => (
  <div style={{ minWidth: 0, ...(colSpan ? { gridColumn: `span ${colSpan}` } : {}) }}>
    <FieldLabel muted={muted}>{label}</FieldLabel>
    {children}
  </div>
);

const BillingItemSelector = ({
  items = [],
  selection = {},
  onChange,
  disabled = false,
  muted,
  text,
  isDarkMode,
  borderColor,
  currency = 'CLP',
  documentType = '',
  isReplacement = false,
  onReplacementChange,
  claimedItemQuantities = {},
  showAccumulatedTotal = true,
}) => {
  const visibleItems = getSelectableBillingItems(items, claimedItemQuantities, isReplacement);
  const headerBg = isDarkMode ? '#18181b' : '#f9fafb';
  const cellPad = '8px';
  const accumulatedGross = sumSelectedItemsGross(items, selection, documentType);

  const updateSelection = (nextSelection) => {
    onChange?.(nextSelection);
  };

  const toggleItem = (item, checked) => {
    if (disabled) return;
    const itemId = Number(item.id);
    const nextSelection = { ...(selection || {}) };
    if (checked) {
      const availableQty = getAvailableItemQuantity(item, claimedItemQuantities);
      nextSelection[itemId] = availableQty > 0 ? availableQty : Number(item.quantity || 0);
    } else {
      delete nextSelection[itemId];
    }
    updateSelection(nextSelection);
  };

  const changeQuantity = (item, rawValue) => {
    if (disabled) return;
    const itemId = Number(item.id);
    const availableQty = getAvailableItemQuantity(item, claimedItemQuantities);
    const qty = Math.min(availableQty, Math.max(0, Number(rawValue || 0)));
    const nextSelection = { ...(selection || {}) };
    if (qty > 0) nextSelection[itemId] = qty;
    else delete nextSelection[itemId];
    updateSelection(nextSelection);
  };

  return (
    <div>
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px',
          fontSize: '12px',
          fontWeight: 600,
          color: text,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={isReplacement}
          disabled={disabled}
          onChange={(event) => onReplacementChange?.(event.target.checked)}
        />
        Documento por reposición
      </label>
      <div
        style={{
          border: `1px solid ${borderColor}`,
          borderRadius: '8px',
          overflow: 'hidden',
          background: isDarkMode ? '#111113' : '#ffffff',
        }}
      >
        <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: headerBg }}>
                <th style={{ width: 36, padding: cellPad, textAlign: 'center', color: muted, fontWeight: 600 }} />
                <th style={{ padding: cellPad, textAlign: 'left', color: muted, fontWeight: 600 }}>Producto</th>
                <th style={{ padding: cellPad, textAlign: 'left', color: muted, fontWeight: 600 }}>Color</th>
                <th style={{ padding: cellPad, textAlign: 'right', color: muted, fontWeight: 600 }}>Cantidad</th>
                <th style={{ padding: cellPad, textAlign: 'right', color: muted, fontWeight: 600 }}>Precio unit.</th>
                <th style={{ padding: cellPad, textAlign: 'right', color: muted, fontWeight: 600 }}>Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '14px', textAlign: 'center', color: muted }}>
                    {isReplacement
                      ? 'No hay ítems disponibles en la cotización.'
                      : 'Todos los ítems ya están asignados a otros documentos. Marca reposición para volver a facturarlos.'}
                  </td>
                </tr>
              ) : (
                visibleItems.map((item) => {
                  const itemId = Number(item.id);
                  const checked = Number(selection?.[itemId] || 0) > 0;
                  const selectedQty = Number(selection?.[itemId] || 0);
                  const availableQty = getAvailableItemQuantity(item, claimedItemQuantities);
                  const unit = String(item.unit || '').trim();
                  return (
                    <tr key={item.id} style={{ borderTop: `1px solid ${borderColor}` }}>
                      <td style={{ padding: cellPad, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={(event) => toggleItem(item, event.target.checked)}
                        />
                      </td>
                      <td style={{ padding: cellPad, color: text, fontWeight: checked ? 700 : 500 }}>
                        {item.item_name || item.product_sku || item.item_code || 'Ítem'}
                      </td>
                      <td style={{ padding: cellPad, color: muted }}>
                        {item.color || item.color_code || '—'}
                      </td>
                      <td style={{ padding: cellPad, textAlign: 'right' }}>
                        {checked ? (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                            <input
                              type="number"
                              min="0"
                              max={availableQty}
                              step="any"
                              value={selectedQty}
                              disabled={disabled}
                              onChange={(event) => changeQuantity(item, event.target.value)}
                              style={{
                                width: 72,
                                height: 28,
                                borderRadius: 6,
                                border: `1px solid ${borderColor}`,
                                background: isDarkMode ? '#18181b' : '#fff',
                                color: text,
                                textAlign: 'right',
                                fontSize: 12,
                                padding: '0 6px',
                              }}
                            />
                            {unit ? <span style={{ color: muted, fontSize: 11 }}>{unit}</span> : null}
                          </div>
                        ) : (
                          <span style={{ color: muted, whiteSpace: 'nowrap' }}>
                            {formatItemQuantity(item, availableQty)}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: cellPad, color: text, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {money(item.unit_price, item.currency || currency)}
                      </td>
                      <td style={{ padding: cellPad, color: checked ? text : muted, textAlign: 'right', fontWeight: checked ? 700 : 500, whiteSpace: 'nowrap' }}>
                        {checked ? money(getItemLineGrossForQuantity(item, selectedQty), item.currency || currency) : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {showAccumulatedTotal ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 12px',
              borderTop: `1px solid ${borderColor}`,
              background: headerBg,
            }}
          >
            <span style={{ fontSize: '12px', color: muted, fontWeight: 600 }}>Monto acumulado c/IVA</span>
            <span style={{ fontSize: '13px', color: text, fontWeight: 800 }}>
              {money(Math.abs(accumulatedGross), currency)}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const SummaryInfo = ({ label, value, hint, muted, text }) => (
  <div style={{ minWidth: 0 }}>
    <FieldLabel muted={muted}>{label}</FieldLabel>
    <div style={{ fontSize: '13px', fontWeight: 600, color: text, wordBreak: 'break-word', lineHeight: 1.35 }}>
      {value || '—'}
    </div>
    {hint ? (
      <div style={{ fontSize: '11px', color: muted, marginTop: 2, lineHeight: 1.35 }}>{hint}</div>
    ) : null}
  </div>
);

const SalesDetailModal = ({
  isOpen = false,
  isDarkMode = false,
  onClose,
  detail = null,
  onSave,
  isSaving = false,
  documentCandidates = [],
  documentCandidatesError = '',
  isLoadingDocumentCandidates = false,
  isLinkingDocument = false,
  onSearchDocuments,
  onSetDocumentLink,
  onConfirmDocumentLink,
  onAddManualBillingDocument,
  onDeleteManualBillingDocument,
  onReconcileManualBillingDocument,
  quotationOptions = [],
  onAddDelivery,
  isAddingDelivery = false,
}) => {
  const sale = detail?.sale || {};
  const items = detail?.items || [];
  const deliveries = detail?.deliveries || [];
  const legs = detail?.legs || [];
  const linkedDocuments = detail?.linked_documents || [];
  const manualBillingDocuments = detail?.manual_billing_documents || [];
  const billingDocuments = useMemo(
    () => mergeBillingDocuments(linkedDocuments, manualBillingDocuments),
    [linkedDocuments, manualBillingDocuments]
  );
  const invoiceLinkStatus = sale?.invoice_link_status || 'none';
  const claimedBillingItemQuantities = useMemo(
    () => getClaimedBillingItemQuantities(billingDocuments, items),
    [billingDocuments, items]
  );

  const [showDocumentPicker, setShowDocumentPicker] = useState(false);
  const [billingAddMenuOpen, setBillingAddMenuOpen] = useState(false);
  const [billingAddMode, setBillingAddMode] = useState(null);
  const [manualBillingDraft, setManualBillingDraft] = useState({
    documentType: '',
    docNumber: '',
    docDate: '',
    selectedItems: {},
    isReplacement: false,
  });
  const [pendingRegisterDocument, setPendingRegisterDocument] = useState(null);
  const [pendingRegisterSelection, setPendingRegisterSelection] = useState({});
  const [pendingRegisterIsReplacement, setPendingRegisterIsReplacement] = useState(false);
  const [reconcilingManualDoc, setReconcilingManualDoc] = useState(null);
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('');
  const [quotePanelOpen, setQuotePanelOpen] = useState(false);
  const [saleCodeError, setSaleCodeError] = useState('');
  const [isCheckingSaleCode, setIsCheckingSaleCode] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfPreviewError, setPdfPreviewError] = useState('');
  const [isBuildingPdfPreview, setIsBuildingPdfPreview] = useState(false);

  const [newDeliveryTipo, setNewDeliveryTipo] = useState('Despacho Completo');
  const [hasPurchaseOrder, setHasPurchaseOrder] = useState(true);
  const [isEditingCommercialSummary, setIsEditingCommercialSummary] = useState(false);
  const resumenRef = useRef(null);

  const [formData, setFormData] = useState({
    sale_code: '',
    deal_close_date: '',
    quotation_id: '',
    purchase_order_code: '',
    purchase_order_received_date: '',
    purchase_order_accepted_date: '',
    invoice_number: '',
    invoice_date: '',
    dispatch_guide: '',
    estimated_cost: '0',
    purchase_contact_name: '',
    purchase_contact_email: '',
    purchase_contact_phone: '',
    delivery_days: '0',
    delivery_place: '',
    payment_terms: '',
    receiver_name: '',
    logistics_notes: '',
    transport_photos: '',
    dispatch_guides_json: '',
    invoices_json: '',
    claims_notes: '',
    replacement_notes: '',
    conformity_status: '',
    conformity_date: '',
    quotation_comments: '',
    quotation_observations: '',
  });

  const [openSections, setOpenSections] = useState(() =>
    Object.fromEntries(SECTION_KEYS.map((key) => [key, false]))
  );

  useEffect(() => {
    if (!isOpen || !sale) return;
    setFormData({
      sale_code: sale.sale_code || '',
      deal_close_date: toYmd(sale.quotation_closing_date ?? sale.deal_close_date),
      quotation_id: sale.quotation_id ? String(sale.quotation_id) : '',
      purchase_order_code: sale.purchase_order_code || '',
      purchase_order_received_date: toYmd(sale.purchase_order_received_date),
      purchase_order_accepted_date: toYmd(sale.purchase_order_accepted_date),
      invoice_number: sale.invoice_number || '',
      invoice_date: toYmd(sale.invoice_date),
      dispatch_guide: sale.dispatch_guide || '',
      estimated_cost: String(sale.estimated_cost ?? 0),
      purchase_contact_name: sale.purchase_contact_name || '',
      purchase_contact_email: sale.purchase_contact_email || '',
      purchase_contact_phone: sale.purchase_contact_phone || '',
      delivery_days: String(sale.delivery_days ?? sale.quotation_delivery_days ?? 0),
      delivery_place: sale.delivery_place || sale.quotation_delivery_place || '',
      payment_terms: sale.payment_terms || sale.quotation_payment_terms || '',
      receiver_name: sale.receiver_name || '',
      logistics_notes: sale.logistics_notes || '',
      transport_photos: sale.transport_photos || '',
      dispatch_guides_json:
        typeof sale.dispatch_guides_json === 'string'
          ? sale.dispatch_guides_json
          : sale.dispatch_guides_json
            ? JSON.stringify(sale.dispatch_guides_json)
            : '',
      invoices_json:
        typeof sale.invoices_json === 'string'
          ? sale.invoices_json
          : sale.invoices_json
            ? JSON.stringify(sale.invoices_json)
            : '',
      claims_notes: sale.claims_notes || '',
      replacement_notes: sale.replacement_notes || '',
      conformity_status: sale.conformity_status || '',
      conformity_date: toYmd(sale.conformity_date),
      quotation_comments: (sale.quotation_comments || '').split(';').filter(Boolean).join('\n'),
      quotation_observations: sale.quotation_observations || '',
    });
    setHasPurchaseOrder(resolveHasPurchaseOrder(sale));
    setIsEditingCommercialSummary(false);
    setOpenSections(Object.fromEntries(SECTION_KEYS.map((key) => [key, false])));
    setShowDocumentPicker(false);
    const method = String(sale.delivery_method || sale.quotation_delivery_method || '').toLowerCase();
    setNewDeliveryTipo(method.includes('retiro') ? 'Retiro en Bodega' : 'Despacho Completo');
    setDocumentSearch('');
    setDocumentTypeFilter('');
    setBillingAddMenuOpen(false);
    setBillingAddMode(null);
    setPendingRegisterDocument(null);
    setPendingRegisterSelection({});
    setPendingRegisterIsReplacement(false);
    setReconcilingManualDoc(null);
    setManualBillingDraft({
      documentType: '',
      docNumber: '',
      docDate: '',
      selectedItems: {},
      isReplacement: false,
    });
    setQuotePanelOpen(false);
    setSaleCodeError('');
    setPdfPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setPdfPreviewError('');
  }, [isOpen, sale?.id]);

  useEffect(() => {
    if (!quotePanelOpen || !isOpen) return undefined;

    let cancelled = false;
    const clientAddress = [
      sale.organization_address,
      sale.organization_commune,
      sale.organization_region,
    ]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(', ');

    const run = async () => {
      setIsBuildingPdfPreview(true);
      setPdfPreviewError('');
      try {
        const url = await buildQuotationPdfObjectUrl({
          quotation_code: sale.quotation_code || '',
          quotation_date: toYmd(sale.quotation_date) || sale.quotation_date,
          validity_days: sale.validity_days,
          organization_name: sale.organization_name,
          organization_rut: sale.organization_rut,
          client_address: clientAddress || sale.delivery_address || sale.delivery_place || '',
          contact_name: sale.contact_full_name,
          contact_email: sale.contact_email,
          contact_phone: sale.contact_phone,
          executive_name: sale.executive_full_name,
          executive_email: sale.executive_email,
          executive_phone: sale.executive_phone,
          payment_terms: formData.payment_terms || sale.payment_terms || sale.quotation_payment_terms,
          delivery_days: formData.delivery_days ?? sale.delivery_days ?? sale.quotation_delivery_days,
          delivery_method:
            sale.delivery_method || sale.quotation_delivery_method || 'Despacho',
          delivery_place: formData.delivery_place || sale.delivery_place,
          channel: sale.channel || sale.quotation_channel,
          tender: sale.tender,
          comments: formData.quotation_comments || sale.quotation_comments,
          items,
        });
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setPdfPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (error) {
        if (!cancelled) {
          setPdfPreviewError(error?.message || 'No se pudo generar la previsualización PDF.');
        }
      } finally {
        if (!cancelled) setIsBuildingPdfPreview(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    quotePanelOpen,
    isOpen,
    sale,
    items,
    formData.payment_terms,
    formData.delivery_days,
    formData.delivery_place,
    formData.quotation_comments,
  ]);

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    };
  }, [pdfPreviewUrl]);

  const totalItems = useMemo(
    () => items.reduce((acc, it) => acc + Number(it.line_total || 0), 0),
    [items]
  );

  const saleChannel = useMemo(
    () => String(sale.channel || sale.quotation_channel || '').trim(),
    [sale.channel, sale.quotation_channel]
  );

  const saleTender = useMemo(() => String(sale.tender || '').trim(), [sale.tender]);

  const saleCloseDate = formData.deal_close_date
    || toYmd(sale.quotation_closing_date ?? sale.deal_close_date);

  const modalTitle = useMemo(() => {
    const saleCode = String(formData.sale_code || sale.sale_code || '').trim();
    const organizationName = String(sale.organization_name || '').trim();
    const closeDateLabel = formatDateEs(saleCloseDate);
    const mutedTitle = isDarkMode ? '#a1a1aa' : '#6b7280';
    return (
      <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '8px' }}>
        <span>{saleCode ? `Venta ${saleCode}` : 'Venta'}</span>
        {organizationName ? (
          <>
            <span style={{ color: mutedTitle, fontWeight: 500 }}>·</span>
            <span style={{ fontWeight: 600 }}>{organizationName}</span>
          </>
        ) : null}
        {closeDateLabel ? (
          <>
            <span style={{ color: mutedTitle, fontWeight: 500 }}>·</span>
            <span style={{ fontWeight: 600 }}>{closeDateLabel}</span>
          </>
        ) : null}
      </span>
    );
  }, [
    formData.sale_code,
    sale.sale_code,
    sale.organization_name,
    saleCloseDate,
    isDarkMode,
  ]);

  const modalSubtitle = useMemo(() => {
    const quotationCode = String(sale.quotation_code || '').trim();
    return joinHeaderParts([quotationCode, saleChannel, saleTender]);
  }, [sale.quotation_code, saleChannel, saleTender]);

  const executiveName = sale.executive_full_name || sale.user_name || '';
  const executiveHint = [sale.executive_email, sale.executive_phone].map((v) => String(v || '').trim()).filter(Boolean).join(' · ');
  const contactName = sale.contact_full_name || sale.contact_name || '';
  const contactHint = [sale.contact_email, sale.contact_phone].map((v) => String(v || '').trim()).filter(Boolean).join(' · ');
  const clientAddressSummary = [
    sale.organization_address,
    sale.organization_commune,
    sale.organization_region,
  ]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ');

  const resumenPanelStyle = {
    border: `1px solid ${isDarkMode ? '#3f3f46' : '#e5e7eb'}`,
    borderRadius: '10px',
    padding: '12px',
    background: isDarkMode ? '#18181b' : '#f9fafb',
  };

  const filteredCandidates = useMemo(() => {
    const typeNeedle = String(documentTypeFilter || '').trim().toLowerCase();
    return (documentCandidates || [])
      .filter((document) => {
        if (document.is_linked) return false;
        if (!typeNeedle) return true;
        const hay = String(document.document_type || '').toLowerCase();
        if (typeNeedle.includes('crédito') || typeNeedle.includes('credito')) {
          return hay.includes('crédito') || hay.includes('credito') || hay.includes('nc');
        }
        if (typeNeedle.includes('débito') || typeNeedle.includes('debito')) {
          return hay.includes('débito') || hay.includes('debito') || hay.includes('nd');
        }
        return hay.includes(typeNeedle);
      })
      .sort((a, b) => Number(b.match_score || 0) - Number(a.match_score || 0));
  }, [documentCandidates, documentTypeFilter]);

  const hasPendingInvoiceFolio = useMemo(
    () =>
      invoiceLinkStatus === 'pending' &&
      Boolean(String(formData.invoice_number || sale.invoice_number || '').trim()),
    [invoiceLinkStatus, formData.invoice_number, sale.invoice_number]
  );

  const sectionStatus = useMemo(() => {
    const purchase = !hasPurchaseOrder
      ? 'no_po'
      : getFillStatus([
          isFilledValue(formData.purchase_order_code),
          isFilledValue(formData.purchase_order_received_date),
          isFilledValue(formData.purchase_order_accepted_date),
          isFilledValue(formData.purchase_contact_name),
          isFilledValue(formData.purchase_contact_email),
          isFilledValue(formData.purchase_contact_phone),
          isFilledValue(formData.delivery_days, { treatZeroAsEmpty: true }),
          isFilledValue(formData.delivery_place),
        ]);

    const dispatch = getFillStatus([
      isFilledValue(formData.dispatch_guide),
      isFilledValue(formData.receiver_name),
      isFilledValue(formData.transport_photos),
      isFilledValue(formData.dispatch_guides_json),
      legs.length > 0 || deliveries.length > 0,
    ]);

    const billing = getBillingSectionStatus(billingDocuments, sale.total_amount);

    const payments = getFillStatus([
      isFilledValue(formData.payment_terms),
      isFilledValue(formData.conformity_status),
      isFilledValue(formData.conformity_date),
      isFilledValue(formData.logistics_notes),
      isFilledValue(formData.claims_notes),
      isFilledValue(formData.replacement_notes),
    ]);

    const notes = getFillStatus([
      isFilledValue(formData.quotation_comments),
      isFilledValue(formData.quotation_observations),
    ]);

    return { purchase, dispatch, billing, payments, notes };
  }, [formData, hasPurchaseOrder, billingDocuments, legs.length, deliveries.length, sale.total_amount]);

  const allCollapsed = SECTION_KEYS.every((key) => !openSections[key]);

  const handleChange = (field, value) => {
    const next = isChileanPhoneField(field) ? formatChileanMobile(value) : value;
    setFormData((prev) => ({ ...prev, [field]: next }));
    if (field === 'sale_code') setSaleCodeError('');
  };

  const normalizeSaleCode = (value) => String(value || '').trim().replace(/\s+/g, '').toUpperCase();

  const validateSaleCode = async (rawCode) => {
    const code = normalizeSaleCode(rawCode);
    if (!code) {
      setSaleCodeError('El código de venta es obligatorio.');
      return null;
    }
    setIsCheckingSaleCode(true);
    try {
      const params = new URLSearchParams({
        code,
        exclude_id: String(sale.id || 0),
      });
      const res = await authFetch(`${API_BASE_URL}/api/quotations/sales/check-code?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.available) {
        setSaleCodeError(data.detail || `Ya existe otra venta con el código ${code}.`);
        return null;
      }
      setSaleCodeError('');
      return data.sale_code || code;
    } catch (err) {
      setSaleCodeError(err.message || 'No se pudo validar el código de venta.');
      return null;
    } finally {
      setIsCheckingSaleCode(false);
    }
  };

  const toggleSection = (key) => {
    if (key === 'purchase' && !hasPurchaseOrder) return;
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleEditCommercialSummary = () => {
    setIsEditingCommercialSummary(true);
    resumenRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleToggleAllSections = () => {
    const nextOpen = allCollapsed;
    setOpenSections(
      Object.fromEntries(
        SECTION_KEYS.map((key) => [
          key,
          nextOpen && !(key === 'purchase' && !hasPurchaseOrder),
        ])
      )
    );
  };

  const handleSave = async () => {
    if (!onSave) return;
    const validatedCode = await validateSaleCode(formData.sale_code);
    if (!validatedCode) return;
    const payload = {
      ...formData,
      sale_code: validatedCode,
      quotation_id: Number(formData.quotation_id || 0),
      estimated_cost: Number(formData.estimated_cost || 0),
      delivery_days: Number(formData.delivery_days || 0),
      quotation_comments: String(formData.quotation_comments || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .join(';'),
      quotation_observations: String(formData.quotation_observations || '').trim(),
    };
    if (billingDocuments.length > 0) {
      delete payload.invoice_number;
      delete payload.invoice_date;
    }
    delete payload.invoices_json;
    payload.has_purchase_order = hasPurchaseOrder;
    if (!hasPurchaseOrder) {
      payload.purchase_order_code = '';
      payload.purchase_order_received_date = '';
      payload.purchase_order_accepted_date = '';
      payload.purchase_order_amount = 0;
    }
    await onSave(payload);
    setIsEditingCommercialSummary(false);
  };

  const hasPurchaseOrderData = (data = {}) =>
    isFilledValue(data.purchase_order_code)
    || isFilledValue(data.purchase_order_received_date)
    || isFilledValue(data.purchase_order_accepted_date);

  const handlePurchaseOrderToggle = (checked) => {
    if (!checked && hasPurchaseOrderData(formData)) {
      const confirmed = window.confirm(
        'Ya hay información de orden de compra registrada. Si desmarcas "Tiene OC", esos datos se eliminarán al guardar. ¿Deseas continuar?'
      );
      if (!confirmed) return;
    }
    setHasPurchaseOrder(checked);
    if (!checked) {
      setOpenSections((prev) => ({ ...prev, purchase: false }));
      setFormData((prev) => ({
        ...prev,
        purchase_order_code: '',
        purchase_order_received_date: '',
        purchase_order_accepted_date: '',
      }));
    }
  };

  const openAddDocument = (typeHint = '') => {
    setDocumentTypeFilter(typeHint);
    setBillingAddMode('register');
    setShowDocumentPicker(true);
    setPendingRegisterDocument(null);
    setPendingRegisterSelection({});
    onSearchDocuments?.(documentSearch);
  };

  const resetBillingAddFlow = () => {
    setBillingAddMode(null);
    setShowDocumentPicker(false);
    setPendingRegisterDocument(null);
    setPendingRegisterSelection({});
    setPendingRegisterIsReplacement(false);
    setReconcilingManualDoc(null);
    setManualBillingDraft({
      documentType: '',
      docNumber: '',
      docDate: '',
      selectedItems: {},
      isReplacement: false,
    });
    setBillingAddMenuOpen(false);
  };

  const startManualBilling = (documentType) => {
    setBillingAddMode('manual');
    setShowDocumentPicker(false);
    setPendingRegisterDocument(null);
    setPendingRegisterSelection({});
    setPendingRegisterIsReplacement(false);
    setManualBillingDraft({
      documentType,
      docNumber: '',
      docDate: '',
      selectedItems: {},
      isReplacement: false,
    });
    setBillingAddMenuOpen(false);
  };

  const manualDraftAmount = useMemo(
    () => sumSelectedItemsGross(items, manualBillingDraft.selectedItems, manualBillingDraft.documentType),
    [items, manualBillingDraft.selectedItems, manualBillingDraft.documentType]
  );

  const handleRegisterDocumentPick = (document) => {
    const amount = Math.abs(getBillingDocumentAmount(document));
    if (documentAmountMatchesSaleTotal(amount, sale.total_amount)) {
      onSetDocumentLink?.(document, true, { itemSelection: getAllSaleItemSelection(items) });
      resetBillingAddFlow();
      return;
    }
    setPendingRegisterDocument(document);
    setPendingRegisterSelection({});
    setPendingRegisterIsReplacement(false);
  };

  const handleManualReplacementChange = (checked) => {
    setManualBillingDraft((prev) => {
      if (checked) return { ...prev, isReplacement: checked };
      const nextSelection = { ...(prev.selectedItems || {}) };
      Object.keys(nextSelection).forEach((rawId) => {
        const item = items.find((row) => Number(row.id) === Number(rawId));
        const availableQty = getAvailableItemQuantity(item, claimedBillingItemQuantities);
        if (availableQty <= 0) delete nextSelection[rawId];
      });
      return {
        ...prev,
        isReplacement: checked,
        selectedItems: nextSelection,
      };
    });
  };

  const handlePendingReplacementChange = (checked) => {
    setPendingRegisterIsReplacement(checked);
    if (!checked) {
      setPendingRegisterSelection((current) => {
        const nextSelection = { ...current };
        Object.keys(nextSelection).forEach((rawId) => {
          const item = items.find((row) => Number(row.id) === Number(rawId));
          const availableQty = getAvailableItemQuantity(item, claimedBillingItemQuantities);
          if (availableQty <= 0) delete nextSelection[rawId];
        });
        return nextSelection;
      });
    }
  };

  const confirmManualReconciliation = async (registerDocument) => {
    if (!reconcilingManualDoc || !registerDocument?.id) return;
    const docKey = reconcilingManualDoc.id || reconcilingManualDoc.doc_key;
    await onReconcileManualBillingDocument?.(docKey, registerDocument.id);
    setReconcilingManualDoc(null);
  };

  const confirmPendingRegisterLink = async () => {
    if (!pendingRegisterDocument) return;
    if (!hasBillingSelection(pendingRegisterSelection, items)) {
      window.alert('Selecciona al menos un ítem facturado para este documento.');
      return;
    }
    await onSetDocumentLink?.(pendingRegisterDocument, true, {
      itemSelection: pendingRegisterSelection,
      isReplacement: pendingRegisterIsReplacement,
    });
    resetBillingAddFlow();
  };

  const submitManualBillingDocument = async () => {
    const docNumber = String(manualBillingDraft.docNumber || '').trim();
    const docDate = String(manualBillingDraft.docDate || '').trim();
    if (!manualBillingDraft.documentType) {
      window.alert('Selecciona un tipo de documento.');
      return;
    }
    if (!docNumber) {
      window.alert('Ingresa el folio del documento.');
      return;
    }
    if (!docDate) {
      window.alert('Ingresa la fecha del documento.');
      return;
    }
    if (!hasBillingSelection(manualBillingDraft.selectedItems, items)) {
      window.alert('Selecciona al menos un ítem facturado.');
      return;
    }
    const itemQuantities = Object.fromEntries(
      Object.entries(manualBillingDraft.selectedItems || {}).map(([id, qty]) => [String(id), Number(qty)])
    );
    await onAddManualBillingDocument?.({
      document_type: manualBillingDraft.documentType,
      doc_number: docNumber,
      doc_date: docDate,
      quotation_item_ids: billingSelectionToIds(manualBillingDraft.selectedItems, items),
      item_quantities: itemQuantities,
      is_replacement: manualBillingDraft.isReplacement,
    });
    resetBillingAddFlow();
  };

  const tableBorder = isDarkMode ? '#27272a' : '#e5e7eb';
  const muted = isDarkMode ? '#a1a1aa' : '#6b7280';
  const text = isDarkMode ? '#f4f4f5' : '#111827';
  const quotePanelWidth = QUOTATION_PREVIEW_SIDE_PANEL_WIDTH;
  const formPanelWidth = quotePanelOpen ? 760 : 1000; // formulario más estrecho al mostrar PDF
  const toggleHalfWidth = 12; // mitad de 24px → eje central sobre el borde

  const sideToggleStyles = {
    position: 'absolute',
    top: '50%',
    // Eje vertical central del botón alineado con el borde del formulario.
    right: (quotePanelOpen ? quotePanelWidth : 0) - toggleHalfWidth,
    transform: 'translateY(-50%)',
    width: '24px',
    height: '80px',
    backgroundColor: isDarkMode ? '#18181b' : '#ffffff',
    border: `2px solid ${tableBorder}`,
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: text,
    zIndex: 20,
    boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.35)' : '0 4px 12px rgba(15,23,42,0.12)',
    padding: 0,
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isDarkMode={isDarkMode}
      size={quotePanelOpen ? 'xxl' : 'xl'}
      title={modalTitle}
      subtitle={modalSubtitle || '—'}
      showCloseButton={false}
      headerActions={!isEditingCommercialSummary ? (
        <button
          type="button"
          onClick={handleEditCommercialSummary}
          style={{ ...btnGhost(isDarkMode), height: '34px' }}
        >
          Editar
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setIsEditingCommercialSummary(false)}
          style={{ ...btnGhost(isDarkMode), height: '34px' }}
        >
          Listo
        </button>
      )}
      style={{
        width: quotePanelOpen ? `${formPanelWidth + quotePanelWidth}px` : `${formPanelWidth}px`,
        maxWidth: 'calc(100vw - 32px)',
      }}
      sidePanelWidth={quotePanelWidth}
      sidePanel={
        quotePanelOpen ? (
          <div
            style={{
              height: '100%',
              borderLeft: `1px solid ${tableBorder}`,
              background: isDarkMode ? '#0a0a0b' : '#e5e7eb',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
              minHeight: 0,
              position: 'relative',
            }}
          >
            {isBuildingPdfPreview ? (
              <div style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}>
                Generando previsualización…
              </div>
            ) : null}
            {pdfPreviewError ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#dc2626', fontSize: 13 }}>
                {pdfPreviewError}
              </div>
            ) : null}
            {!isBuildingPdfPreview && !pdfPreviewError && pdfPreviewUrl ? (
              <iframe
                title={`PDF ${sale.quotation_code || 'cotización'}`}
                src={pdfPreviewUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block',
                  background: '#ffffff',
                  flex: 1,
                  minHeight: 0,
                }}
              />
            ) : null}
            {!isBuildingPdfPreview && !pdfPreviewError && !pdfPreviewUrl ? (
              <div style={{ padding: 24, textAlign: 'center', color: muted, fontSize: 13 }}>
                Sin previsualización disponible.
              </div>
            ) : null}
          </div>
        ) : null
      }
      edgeControl={(
        <button
          type="button"
          onClick={() => setQuotePanelOpen((v) => !v)}
          style={sideToggleStyles}
          title={quotePanelOpen ? 'Ocultar PDF de cotización' : 'Mostrar PDF de cotización'}
          aria-label={quotePanelOpen ? 'Ocultar PDF de cotización' : 'Mostrar PDF de cotización'}
        >
          {quotePanelOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      )}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', width: '100%' }}>
          <button type="button" onClick={onClose} style={{ ...btnGhost(isDarkMode), height: '34px' }}>
            Cerrar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isCheckingSaleCode || Boolean(saleCodeError)}
            style={{ ...btnPrimary, height: '34px', opacity: isSaving || isCheckingSaleCode || saleCodeError ? 0.7 : 1 }}
          >
            {isSaving || isCheckingSaleCode ? 'Guardando...' : 'Guardar detalle'}
          </button>
        </div>
      )}
    >
      <div style={{ display: 'grid', gap: '14px', minWidth: 0 }}>
            <section ref={resumenRef} style={resumenPanelStyle}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 700, color: text }}>
                Resumen comercial
              </h4>

              {isEditingCommercialSummary ? (
                <>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', color: muted, lineHeight: 1.45 }}>
                    Ajusta el código de venta, la fecha de cierre o la cotización vinculada. El resto del formulario sigue editable.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
                    <FormField label="Código venta" muted={muted}>
                      <input
                        style={{
                          ...inputBase(isDarkMode),
                          borderColor: saleCodeError ? '#dc2626' : undefined,
                        }}
                        value={formData.sale_code}
                        onChange={(e) => handleChange('sale_code', e.target.value)}
                        onBlur={() => {
                          if (normalizeSaleCode(formData.sale_code) !== normalizeSaleCode(sale.sale_code)) {
                            validateSaleCode(formData.sale_code);
                          } else {
                            setSaleCodeError('');
                          }
                        }}
                        placeholder="V001"
                      />
                      {saleCodeError ? (
                        <div style={{ fontSize: '11px', color: '#dc2626', marginTop: 4 }}>{saleCodeError}</div>
                      ) : (
                        <div style={{ fontSize: '10px', color: muted, marginTop: 4 }}>
                          Único entre ventas activas (no distingue mayúsculas).
                        </div>
                      )}
                    </FormField>
                    <FormField label="Fecha venta" muted={muted}>
                      <input
                        type="date"
                        style={inputBase(isDarkMode)}
                        value={formData.deal_close_date}
                        onChange={(e) => handleChange('deal_close_date', e.target.value)}
                      />
                    </FormField>
                    <FormField label="Cotización asociada" muted={muted}>
                      <select
                        style={inputBase(isDarkMode)}
                        value={formData.quotation_id}
                        onChange={(e) => handleChange('quotation_id', e.target.value)}
                      >
                        {!formData.quotation_id ? <option value="">Seleccionar cotización</option> : null}
                        {quotationOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: '12px 16px',
                  }}
                >
                  <SummaryInfo
                    label="Fecha cotización"
                    value={dateText(sale.quotation_date)}
                    hint={sale.validity_days ? `Vigencia: ${sale.validity_days} días` : ''}
                    muted={muted}
                    text={text}
                  />
                  <SummaryInfo
                    label="RUT cliente"
                    value={sale.organization_rut}
                    muted={muted}
                    text={text}
                  />
                  <SummaryInfo
                    label="Ejecutivo"
                    value={executiveName}
                    hint={executiveHint}
                    muted={muted}
                    text={text}
                  />
                  <SummaryInfo
                    label="Contacto"
                    value={contactName}
                    hint={contactHint}
                    muted={muted}
                    text={text}
                  />
                  <SummaryInfo
                    label="Monto neto"
                    value={money(sale.total_amount, sale.currency)}
                    hint={`c/IVA: ${money(grossAmountConIva(sale.total_amount), sale.currency)}`}
                    muted={muted}
                    text={text}
                  />
                  <SummaryInfo
                    label="Ítems"
                    value={`${items.length} línea${items.length === 1 ? '' : 's'}`}
                    hint={totalItems ? `Suma líneas: ${money(totalItems, sale.currency)}` : ''}
                    muted={muted}
                    text={text}
                  />
                  <SummaryInfo
                    label="Dirección cliente"
                    value={clientAddressSummary}
                    muted={muted}
                    text={text}
                  />
                  {Number(sale.equivalent_gallons) > 0 ? (
                    <SummaryInfo
                      label="Galones equivalentes"
                      value={Number(sale.equivalent_gallons).toLocaleString('es-CL')}
                      muted={muted}
                      text={text}
                    />
                  ) : null}
                </div>
              )}
            </section>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" onClick={handleToggleAllSections} style={btnGhost(isDarkMode)}>
              {allCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
              {allCollapsed ? 'Expandir todo' : 'Colapsar todo'}
            </button>
          </div>

          <CollapsibleSection
            title="Orden de compra"
            status={sectionStatus.purchase}
            isOpen={openSections.purchase}
            expandable={hasPurchaseOrder}
            onToggle={() => toggleSection('purchase')}
            isDarkMode={isDarkMode}
            headerAddon={
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginLeft: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: text,
                }}
              >
                <input
                  type="checkbox"
                  checked={hasPurchaseOrder}
                  onChange={(e) => handlePurchaseOrderToggle(e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: '#0f766e', margin: 0 }}
                />
                Tiene OC
              </label>
            }
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
              <FormField label="Código OC" muted={muted}>
                <input style={inputBase(isDarkMode)} value={formData.purchase_order_code} onChange={(e) => handleChange('purchase_order_code', e.target.value)} placeholder="Ej. OC-2024-001" />
              </FormField>
              <FormField label="Fecha recepción OC" muted={muted}>
                <input type="date" style={inputBase(isDarkMode)} value={formData.purchase_order_received_date} onChange={(e) => handleChange('purchase_order_received_date', e.target.value)} />
              </FormField>
              <FormField label="Fecha aceptación OC" muted={muted}>
                <input type="date" style={inputBase(isDarkMode)} value={formData.purchase_order_accepted_date} onChange={(e) => handleChange('purchase_order_accepted_date', e.target.value)} />
              </FormField>
              <FormField label="Contacto de compra" muted={muted}>
                <input style={inputBase(isDarkMode)} value={formData.purchase_contact_name} onChange={(e) => handleChange('purchase_contact_name', e.target.value)} placeholder="Nombre contacto" />
              </FormField>
              <FormField label="Email contacto" muted={muted}>
                <input style={inputBase(isDarkMode)} value={formData.purchase_contact_email} onChange={(e) => handleChange('purchase_contact_email', e.target.value)} placeholder="correo@empresa.cl" />
              </FormField>
              <FormField label="Teléfono contacto" muted={muted}>
                <input
                  style={inputBase(isDarkMode)}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  maxLength={PHONE_MAX_LENGTH}
                  value={formatChileanMobile(formData.purchase_contact_phone)}
                  onChange={(e) => handleChange('purchase_contact_phone', e.target.value)}
                  placeholder={CHILE_MOBILE_PLACEHOLDER}
                />
              </FormField>
              <FormField label="Días hábiles entrega" muted={muted}>
                <input type="number" min="0" style={inputBase(isDarkMode)} value={formData.delivery_days} onChange={(e) => handleChange('delivery_days', e.target.value)} placeholder="Ej. 5" />
              </FormField>
              <FormField label="Dirección / lugar de entrega" muted={muted} colSpan={2}>
                <input style={inputBase(isDarkMode)} value={formData.delivery_place} onChange={(e) => handleChange('delivery_place', e.target.value)} placeholder="Dirección o lugar de entrega" />
              </FormField>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Despacho"
            status={sectionStatus.dispatch}
            isOpen={openSections.dispatch}
            onToggle={() => toggleSection('dispatch')}
            isDarkMode={isDarkMode}
            headerActions={
              <div
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                onClick={(e) => e.stopPropagation()}
              >
                <select
                  value={newDeliveryTipo}
                  onChange={(e) => setNewDeliveryTipo(e.target.value)}
                  disabled={isAddingDelivery || !onAddDelivery}
                  title="Desde venta solo Despacho Completo o Retiro en Bodega"
                  style={{
                    ...inputBase(isDarkMode),
                    height: '32px',
                    minWidth: '180px',
                    fontSize: '12px',
                    padding: '0 8px',
                  }}
                >
                  <option value="Despacho Completo">Despacho Completo</option>
                  <option value="Retiro en Bodega">Retiro en Bodega</option>
                </select>
                <button
                  type="button"
                  onClick={() => onAddDelivery?.(newDeliveryTipo)}
                  disabled={isAddingDelivery || !onAddDelivery}
                  style={btnPrimary}
                  title="Crear entrega (Parcial y Reposición se gestionan en Despachos)"
                >
                  <Plus size={14} />
                  {isAddingDelivery ? 'Creando…' : 'Nueva entrega'}
                </button>
              </div>
            }
          >
            <div style={{ fontSize: '11px', color: muted, marginBottom: '8px', lineHeight: 1.4 }}>
              Desde venta solo puedes asignar <strong>Despacho Completo</strong> o{' '}
              <strong>Retiro en Bodega</strong>. Para <strong>Despacho Parcial</strong> o{' '}
              <strong>Reposición</strong>, usa Despachos → Entregas.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
              <FormField label="Guía despacho principal" muted={muted}>
                <input style={inputBase(isDarkMode)} value={formData.dispatch_guide} onChange={(e) => handleChange('dispatch_guide', e.target.value)} placeholder="N° guía" />
              </FormField>
              <FormField label="Persona que recepciona" muted={muted}>
                <input style={inputBase(isDarkMode)} value={formData.receiver_name} onChange={(e) => handleChange('receiver_name', e.target.value)} placeholder="Nombre receptor" />
              </FormField>
              <FormField label="Fotos transporte" muted={muted}>
                <input style={inputBase(isDarkMode)} value={formData.transport_photos} onChange={(e) => handleChange('transport_photos', e.target.value)} placeholder="URLs separadas por coma" />
              </FormField>
            </div>
            <div style={{ marginTop: '10px' }}>
              <FormField label="Guías adicionales" muted={muted}>
                <textarea rows={3} style={inputBase(isDarkMode)} value={formData.dispatch_guides_json} onChange={(e) => handleChange('dispatch_guides_json', e.target.value)} placeholder="Guías y fechas (JSON o texto)" />
              </FormField>
            </div>

            <div style={{ marginTop: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
                <h5 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: text }}>Tramos asociados</h5>
                <span style={{ fontSize: '12px', color: muted }}>
                  {legs.length} tramo{legs.length === 1 ? '' : 's'} · {deliveries.length} entrega
                  {deliveries.length === 1 ? '' : 's'}
                </span>
              </div>
              <div style={{ maxHeight: '220px', overflow: 'auto', border: `1px solid ${tableBorder}`, borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: isDarkMode ? '#18181b' : '#f9fafb' }}>
                      <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>Tramo</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>Entrega</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>Viaje</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>Transportista</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>ETA</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>Origen → Destino</th>
                      <th style={{ textAlign: 'right', padding: '8px', color: muted, fontWeight: 600 }}>Costo</th>
                      <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {legs.length === 0 ? (
                      <tr>
                        <td colSpan={8} style={{ padding: '16px', textAlign: 'center', color: muted }}>
                          No hay tramos asociados a esta venta.
                        </td>
                      </tr>
                    ) : (
                      legs.map((leg) => (
                        <tr key={leg.id} style={{ borderTop: `1px solid ${tableBorder}` }}>
                          <td style={{ padding: '8px', color: text, fontWeight: 600 }}>{leg.leg_code || '—'}</td>
                          <td style={{ padding: '8px', color: text }}>{leg.delivery_code || '—'}</td>
                          <td style={{ padding: '8px', color: text }}>{leg.trip_code || 'Sin viaje'}</td>
                          <td style={{ padding: '8px', color: text }}>{leg.carrier_name || '—'}</td>
                          <td style={{ padding: '8px', color: text }}>{dateText(leg.eta_close)}</td>
                          <td style={{ padding: '8px', color: text }}>
                            {[leg.origin_location, leg.destination_location].filter(Boolean).join(' → ') || '—'}
                          </td>
                          <td style={{ padding: '8px', color: text, textAlign: 'right' }}>
                            {Number(leg.allocated_cost || 0).toLocaleString('es-CL')}
                          </td>
                          <td style={{ padding: '8px', color: text }}>{leg.leg_status || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Facturación"
            status={sectionStatus.billing}
            isOpen={openSections.billing}
            onToggle={() => toggleSection('billing')}
            isDarkMode={isDarkMode}
            headerActions={(
              <div
                style={{ position: 'relative', display: 'inline-flex' }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => setBillingAddMenuOpen((open) => !open)}
                  disabled={isLinkingDocument}
                  style={btnPrimary}
                  title="Agregar documento de facturación"
                >
                  <Plus size={14} />
                  Agregar documento
                  <ChevronDown size={14} />
                </button>
                {billingAddMenuOpen ? (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      right: 0,
                      minWidth: '220px',
                      border: `1px solid ${tableBorder}`,
                      borderRadius: '8px',
                      background: isDarkMode ? '#18181b' : '#ffffff',
                      boxShadow: isDarkMode ? '0 8px 24px rgba(0,0,0,0.45)' : '0 8px 24px rgba(15,23,42,0.12)',
                      zIndex: 30,
                      overflow: 'hidden',
                    }}
                  >
                    {BILLING_DOC_TYPE_OPTIONS.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => startManualBilling(option.documentType)}
                        style={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          background: 'transparent',
                          color: text,
                          padding: '10px 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                    <div style={{ height: 1, background: tableBorder }} />
                    <button
                      type="button"
                      onClick={() => openAddDocument('')}
                      style={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        gap: '8px',
                        textAlign: 'left',
                        border: 'none',
                        background: 'transparent',
                        color: text,
                        padding: '10px 12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      <Link2 size={14} />
                      {REGISTER_BILLING_OPTION.label}
                    </button>
                  </div>
                ) : null}
              </div>
            )}
          >
            {hasPendingInvoiceFolio ? (
              <div
                style={{
                  marginTop: '10px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${isDarkMode ? '#854d0e' : '#fde68a'}`,
                  background: isDarkMode ? 'rgba(113, 63, 18, 0.25)' : '#fffbeb',
                  color: isDarkMode ? '#fde68a' : '#92400e',
                  fontSize: '12px',
                  lineHeight: 1.45,
                }}
              >
                Folio <strong>{formData.invoice_number || sale.invoice_number}</strong> registrado en la venta.
                Aún no está vinculado al Libro de Ventas contable. Al importar el libro, si el folio coincide se listará como candidato para vincularlo manualmente.
              </div>
            ) : null}

            {invoiceLinkStatus === 'suggested' ? (
              <div
                style={{
                  marginTop: '10px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${isDarkMode ? '#6d28d9' : '#ddd6fe'}`,
                  background: isDarkMode ? 'rgba(109, 40, 217, 0.2)' : '#f5f3ff',
                  color: isDarkMode ? '#ddd6fe' : '#5b21b6',
                  fontSize: '12px',
                  lineHeight: 1.45,
                }}
              >
                Hay vínculos pendientes de aprobación. Confírmalos o recházalos en la tabla inferior.
              </div>
            ) : null}

            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <div>
                <h5 style={{ margin: 0, fontSize: '13px', color: text }}>Documentos de facturación</h5>
                <span style={{ fontSize: '11px', color: muted }}>
                  {billingDocuments.length} documento{billingDocuments.length === 1 ? '' : 's'}
                </span>
              </div>
            </div>

            {billingAddMode === 'manual' ? (
              <div
                style={{
                  marginTop: '10px',
                  border: `1px solid ${tableBorder}`,
                  borderRadius: '8px',
                  padding: '12px',
                  background: isDarkMode ? '#111113' : '#fafafa',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: text }}>
                    Nuevo documento manual · {manualBillingDraft.documentType || 'Documento'}
                  </div>
                  <button type="button" onClick={resetBillingAddFlow} style={btnGhost(isDarkMode)}>
                    Cancelar
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
                  <FormField label="Folio" muted={muted}>
                    <input
                      style={inputBase(isDarkMode)}
                      value={manualBillingDraft.docNumber}
                      onChange={(e) => setManualBillingDraft((prev) => ({ ...prev, docNumber: e.target.value }))}
                      placeholder="N° folio"
                    />
                  </FormField>
                  <FormField label="Fecha" muted={muted}>
                    <input
                      type="date"
                      style={inputBase(isDarkMode)}
                      value={manualBillingDraft.docDate}
                      onChange={(e) => setManualBillingDraft((prev) => ({ ...prev, docDate: e.target.value }))}
                    />
                  </FormField>
                  <FormField label="Monto calculado" muted={muted}>
                    <input
                      style={{ ...inputBase(isDarkMode), fontWeight: 700 }}
                      value={money(Math.abs(manualDraftAmount), sale.currency)}
                      readOnly
                    />
                  </FormField>
                </div>
                <div style={{ marginTop: '10px' }}>
                  <FieldLabel muted={muted}>Ítems facturados</FieldLabel>
                  <BillingItemSelector
                    items={items}
                    selection={manualBillingDraft.selectedItems}
                    onChange={(selectedItems) => setManualBillingDraft((prev) => ({ ...prev, selectedItems }))}
                    isReplacement={manualBillingDraft.isReplacement}
                    onReplacementChange={handleManualReplacementChange}
                    claimedItemQuantities={claimedBillingItemQuantities}
                    documentType={manualBillingDraft.documentType}
                    currency={sale.currency}
                    muted={muted}
                    text={text}
                    isDarkMode={isDarkMode}
                    borderColor={tableBorder}
                    showAccumulatedTotal={false}
                  />
                </div>
                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={submitManualBillingDocument}
                    disabled={isLinkingDocument}
                    style={btnPrimary}
                  >
                    {isLinkingDocument ? 'Guardando…' : 'Agregar documento'}
                  </button>
                </div>
              </div>
            ) : null}

            {pendingRegisterDocument ? (
              <div
                style={{
                  marginTop: '10px',
                  border: `1px solid ${isDarkMode ? '#1d4ed8' : '#bfdbfe'}`,
                  borderRadius: '8px',
                  padding: '12px',
                  background: isDarkMode ? 'rgba(29, 78, 216, 0.15)' : '#eff6ff',
                }}
              >
                <div style={{ fontSize: '12px', color: text, marginBottom: '8px', lineHeight: 1.45 }}>
                  El monto del documento <strong>{pendingRegisterDocument.doc_number || pendingRegisterDocument.doc_code}</strong>
                  {' '}({money(pendingRegisterDocument.total_amount, pendingRegisterDocument.currency)}) no coincide con el total de la venta.
                  Selecciona los ítems facturados en este documento.
                </div>
                <BillingItemSelector
                  items={items}
                  selection={pendingRegisterSelection}
                  onChange={setPendingRegisterSelection}
                  isReplacement={pendingRegisterIsReplacement}
                  onReplacementChange={handlePendingReplacementChange}
                  claimedItemQuantities={claimedBillingItemQuantities}
                  documentType={pendingRegisterDocument?.document_type}
                  currency={sale.currency}
                  muted={muted}
                  text={text}
                  isDarkMode={isDarkMode}
                  borderColor={tableBorder}
                />
                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button type="button" onClick={() => setPendingRegisterDocument(null)} style={btnGhost(isDarkMode)}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={confirmPendingRegisterLink}
                    disabled={isLinkingDocument || !hasBillingSelection(pendingRegisterSelection, items)}
                    style={btnPrimary}
                  >
                    Vincular documento
                  </button>
                </div>
              </div>
            ) : null}

            <div style={{ marginTop: '8px', border: `1px solid ${tableBorder}`, borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: isDarkMode ? '#18181b' : '#f9fafb' }}>
                    <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>Tipo</th>
                    <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>Folio</th>
                    <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>Fecha</th>
                    <th style={{ textAlign: 'right', padding: '8px', color: muted, fontWeight: 600 }}>Monto</th>
                    <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>Ítems</th>
                    <th style={{ textAlign: 'left', padding: '8px', color: muted, fontWeight: 600 }}>Origen</th>
                    <th style={{ textAlign: 'center', padding: '8px', color: muted, fontWeight: 600 }} />
                  </tr>
                </thead>
                <tbody>
                  {billingDocuments.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '14px', color: muted, textAlign: 'center' }}>
                        No hay documentos de facturación. Usa &quot;Agregar documento&quot; para registrar uno.
                      </td>
                    </tr>
                  ) : (
                    billingDocuments.map((document) => {
                      const isSuggested = String(document.link_method || '') === 'suggested';
                      const isManual = document.source === 'manual';
                      const pendingReconciliation = Boolean(isManual && document.pending_reconciliation);
                      const rowKey = isManual ? document.id || document.doc_key : document.id;
                      const invoicedItemQuantities = document.invoiced_item_quantities || document.item_quantities || {};
                      const invoicedItemIds = document.invoiced_item_ids?.length
                        ? document.invoiced_item_ids
                        : (!isManual && documentAmountMatchesSaleTotal(
                          Math.abs(getBillingDocumentAmount(document)),
                          sale.total_amount
                        )
                          ? billingSelectionToIds(getAllSaleItemSelection(items), items)
                          : []);
                      return (
                      <tr key={rowKey} style={{ borderTop: `1px solid ${tableBorder}` }}>
                        <td style={{ padding: '8px', color: text, fontWeight: 600 }}>
                          {document.document_type || 'Documento'}
                          {document.is_replacement ? (
                            <span
                              style={{
                                marginLeft: '6px',
                                fontSize: '10px',
                                fontWeight: 700,
                                color: '#0369a1',
                                border: '1px solid #7dd3fc',
                                borderRadius: '999px',
                                padding: '1px 6px',
                              }}
                            >
                              Reposición
                            </span>
                          ) : null}
                          {isSuggested ? (
                            <span
                              style={{
                                marginLeft: '6px',
                                fontSize: '10px',
                                fontWeight: 700,
                                color: '#7c3aed',
                                border: '1px solid #c4b5fd',
                                borderRadius: '999px',
                                padding: '1px 6px',
                              }}
                            >
                              Sugerido
                            </span>
                          ) : null}
                          {pendingReconciliation ? (
                            <span
                              style={{
                                marginLeft: '6px',
                                fontSize: '10px',
                                fontWeight: 700,
                                color: '#b45309',
                                border: '1px solid #fcd34d',
                                borderRadius: '999px',
                                padding: '1px 6px',
                              }}
                            >
                              Pendiente conciliación
                            </span>
                          ) : null}
                        </td>
                        <td style={{ padding: '8px', color: text }}>{document.doc_number || document.doc_code || '—'}</td>
                        <td style={{ padding: '8px', color: text }}>{dateText(document.doc_date)}</td>
                        <td style={{ padding: '8px', color: text, textAlign: 'right' }}>
                          {money(document.total_amount, document.currency)}
                        </td>
                        <td style={{ padding: '8px', color: muted, maxWidth: '180px' }}>
                          {formatBillingItemsSummary(items, invoicedItemIds, invoicedItemQuantities)}
                        </td>
                        <td style={{ padding: '8px', color: muted }}>
                          {isManual ? 'Manual' : 'Libro contable'}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                            {isSuggested ? (
                              <button
                                type="button"
                                onClick={() => onConfirmDocumentLink?.(document)}
                                disabled={isLinkingDocument}
                                title="Confirmar vínculo sugerido"
                                style={{ ...btnPrimary, height: '28px', fontSize: '11px', padding: '0 8px' }}
                              >
                                Confirmar
                              </button>
                            ) : null}
                            {pendingReconciliation ? (
                              <button
                                type="button"
                                onClick={() => setReconcilingManualDoc(document)}
                                disabled={isLinkingDocument}
                                title="Conciliar con libro contable"
                                style={{ ...btnPrimary, height: '28px', fontSize: '11px', padding: '0 8px' }}
                              >
                                <Link2 size={13} />
                                Conciliar
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => {
                                if (isManual) {
                                  onDeleteManualBillingDocument?.(document.id || document.doc_key);
                                  return;
                                }
                                onSetDocumentLink?.(document, false);
                              }}
                              disabled={isLinkingDocument}
                              title="Eliminar documento"
                              style={{
                                border: `1px solid ${tableBorder}`,
                                background: 'transparent',
                                color: '#dc2626',
                                borderRadius: '7px',
                                padding: '6px',
                                cursor: 'pointer',
                              }}
                            >
                              <Unlink size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {reconcilingManualDoc ? (
              <div
                style={{
                  marginTop: '10px',
                  border: `1px solid ${isDarkMode ? '#b45309' : '#fcd34d'}`,
                  borderRadius: '8px',
                  padding: '12px',
                  background: isDarkMode ? 'rgba(180, 83, 9, 0.12)' : '#fffbeb',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: text }}>
                    Conciliar documento manual · Folio {reconcilingManualDoc.doc_number || '—'}
                  </div>
                  <button type="button" onClick={() => setReconcilingManualDoc(null)} style={btnGhost(isDarkMode)}>
                    Cancelar
                  </button>
                </div>
                <div style={{ fontSize: '12px', color: muted, marginBottom: '8px', lineHeight: 1.45 }}>
                  Selecciona el documento del Libro de Ventas que corresponde a este registro manual.
                </div>
                <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  {(reconcilingManualDoc.reconciliation_candidates || []).length === 0 ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: muted, fontSize: '12px' }}>
                      No hay documentos contables disponibles con el mismo folio.
                    </div>
                  ) : (
                    (reconcilingManualDoc.reconciliation_candidates || []).map((candidate) => (
                      <div
                        key={candidate.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          gap: '10px',
                          alignItems: 'center',
                          padding: '9px 2px',
                          borderTop: `1px solid ${tableBorder}`,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: text, fontSize: '12px', fontWeight: 700 }}>
                            {candidate.document_type || 'Documento'} · {candidate.doc_number || candidate.doc_code}
                          </div>
                          <div style={{ color: muted, fontSize: '11px', marginTop: '2px' }}>
                            {dateText(candidate.doc_date)} · {money(candidate.total_amount, candidate.currency)}
                            {candidate.counterpart_name ? ` · ${candidate.counterpart_name}` : ''}
                            {Number(candidate.match_score || 0) > 0
                              ? ` · Coincidencia ${Math.round(Number(candidate.match_score))}%`
                              : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => confirmManualReconciliation(candidate)}
                          disabled={isLinkingDocument}
                          style={{ ...btnPrimary, height: '30px' }}
                        >
                          Vincular
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : null}

            {billingAddMode === 'register' && showDocumentPicker && !pendingRegisterDocument ? (
              <div style={{ marginTop: '10px', border: `1px solid ${tableBorder}`, borderRadius: '8px', padding: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: text }}>Buscar en libro contable</div>
                  <button type="button" onClick={resetBillingAddFlow} style={btnGhost(isDarkMode)}>
                    Cancelar
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 160px auto', gap: '10px', alignItems: 'end' }}>
                  <FormField label="Buscar documento" muted={muted}>
                    <input
                      style={inputBase(isDarkMode)}
                      value={documentSearch}
                      onChange={(event) => setDocumentSearch(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') onSearchDocuments?.(documentSearch);
                      }}
                      placeholder="Folio, RUT, cliente o tipo"
                    />
                  </FormField>
                  <FormField label="Tipo de documento" muted={muted}>
                    <select
                      style={inputBase(isDarkMode)}
                      value={documentTypeFilter}
                      onChange={(e) => setDocumentTypeFilter(e.target.value)}
                    >
                      {DOC_TYPE_HINTS.filter((h, idx, arr) => arr.findIndex((x) => x.value === h.value) === idx).map((h) => (
                        <option key={`${h.value}-${h.label}`} value={h.value}>
                          {h.label}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <button
                    type="button"
                    onClick={() => onSearchDocuments?.(documentSearch)}
                    disabled={isLoadingDocumentCandidates}
                    style={{ ...btnGhost(isDarkMode), height: '36px', marginBottom: 0 }}
                  >
                    <Search size={14} />
                    Buscar
                  </button>
                </div>
                <div style={{ marginTop: '8px', maxHeight: '260px', overflowY: 'auto' }}>
                  {isLoadingDocumentCandidates ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: muted, fontSize: '12px' }}>Buscando documentos…</div>
                  ) : documentCandidatesError ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#dc2626', fontSize: '12px' }}>{documentCandidatesError}</div>
                  ) : filteredCandidates.length === 0 ? (
                    <div style={{ padding: '12px', textAlign: 'center', color: muted, fontSize: '12px' }}>
                      No hay documentos disponibles para vincular.
                    </div>
                  ) : (
                    filteredCandidates.map((document) => (
                      <div
                        key={document.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr auto',
                          gap: '10px',
                          alignItems: 'center',
                          padding: '9px 2px',
                          borderTop: `1px solid ${tableBorder}`,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ color: text, fontSize: '12px', fontWeight: 700 }}>
                            {document.document_type || 'Documento'} · {document.doc_number || document.doc_code}
                          </div>
                          <div style={{ color: muted, fontSize: '11px', marginTop: '2px' }}>
                            {dateText(document.doc_date)} · {money(document.total_amount, document.currency)}
                            {document.counterpart_name ? ` · ${document.counterpart_name}` : ''}
                            {Number(document.match_score || 0) > 0 ? ` · Coincidencia ${Math.round(Number(document.match_score))}%` : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRegisterDocumentPick(document)}
                          disabled={isLinkingDocument}
                          style={{ ...btnPrimary, height: '30px' }}
                        >
                          Vincular
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : null}
          </CollapsibleSection>

          <CollapsibleSection
            title="Pagos y conformidad"
            status={sectionStatus.payments}
            isOpen={openSections.payments}
            onToggle={() => toggleSection('payments')}
            isDarkMode={isDarkMode}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
              <FormField label="Términos / tipo de pago" muted={muted}>
                <input style={inputBase(isDarkMode)} value={formData.payment_terms} onChange={(e) => handleChange('payment_terms', e.target.value)} placeholder="Ej. 30 días" />
              </FormField>
              <FormField label="Estado cierre conforme" muted={muted}>
                <input style={inputBase(isDarkMode)} value={formData.conformity_status} onChange={(e) => handleChange('conformity_status', e.target.value)} placeholder="Ej. Pendiente" />
              </FormField>
              <FormField label="Fecha conformidad" muted={muted}>
                <input type="date" style={inputBase(isDarkMode)} value={formData.conformity_date} onChange={(e) => handleChange('conformity_date', e.target.value)} />
              </FormField>
            </div>
            <div style={{ marginTop: '10px' }}>
              <FormField label="Notas logísticas / pago" muted={muted}>
                <input style={inputBase(isDarkMode)} value={formData.logistics_notes} onChange={(e) => handleChange('logistics_notes', e.target.value)} placeholder="Notas sobre logística o pago" />
              </FormField>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px' }}>
              <FormField label="Reclamos" muted={muted}>
                <textarea rows={3} style={inputBase(isDarkMode)} value={formData.claims_notes} onChange={(e) => handleChange('claims_notes', e.target.value)} placeholder="Detalle de reclamos" />
              </FormField>
              <FormField label="Reposiciones" muted={muted}>
                <textarea rows={3} style={inputBase(isDarkMode)} value={formData.replacement_notes} onChange={(e) => handleChange('replacement_notes', e.target.value)} placeholder="Detalle de reposiciones" />
              </FormField>
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Notas"
            status={sectionStatus.notes}
            isOpen={openSections.notes}
            onToggle={() => toggleSection('notes')}
            isDarkMode={isDarkMode}
          >
            <div style={{ fontSize: '11px', color: muted, marginBottom: '10px' }}>
              Notas compartidas entre cotización, venta y entregas asociadas. Los cambios se guardan en la cotización.
            </div>
            <FormField label="Comentarios" muted={muted}>
              <textarea
                rows={4}
                style={{ ...inputBase(isDarkMode), height: 'auto', padding: '10px', resize: 'vertical' }}
                value={formData.quotation_comments}
                onChange={(e) => handleChange('quotation_comments', e.target.value)}
                placeholder={'Un comentario por línea\nEj: Requiere certificado técnico'}
              />
            </FormField>
            <div style={{ marginTop: '10px' }}>
              <FormField label="Observaciones internas" muted={muted}>
                <textarea
                  rows={3}
                  style={{ ...inputBase(isDarkMode), height: 'auto', padding: '10px', resize: 'vertical' }}
                  value={formData.quotation_observations}
                  onChange={(e) => handleChange('quotation_observations', e.target.value)}
                  placeholder="No se muestran en el PDF al cliente"
                />
              </FormField>
            </div>
          </CollapsibleSection>
      </div>
    </Modal>
  );
};

export default SalesDetailModal;
